import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { Observable } from "rxjs";
import {
  GbpersonService,
  GbpfandrechtService,
  GrundstueckId,
  LoadPfandrechtSichtGrundstueckResponseMeta,
  PfandrechtBelastungMasterDetailEntryMeta,
  PfandrechtBelastungVersionDetailEntryMeta,
  PfandrechtMasterDetailEntryMeta,
  PfandrechtVersionDetailEntryMeta,
  PfandrechtVersionIdMeta,
  RangregelungEntryMeta,
  RechteBeziehungDetailEntryMeta,
  Teilpfandgruppe,
} from "../gb-api";
import { TimepointService } from "./timepoint.service";
import { AppConfigService } from "./app-config.service";
import { TranslateService } from "@ngx-translate/core";
import { ComparatorService } from "../../shared/services/comparator-service";
import { HistoryMode } from "./HistoryMode";

@Injectable()
export class GrundpfandrechtViewmodelService {
  constructor(
    protected translateService: TranslateService,
    private rechtService: GbpfandrechtService,
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    protected comparatorService: ComparatorService
  ) {}

  loadSichtByGbGrundstueckId(
    grundstueckId: GrundstueckId
  ): Observable<GrundpfandrechtSichtGrundstueckVm> {
    const z = this.timepointService.timepointCtx;
    return this.rechtService
      .loadPfandrechtSichtGrundstueck(
        grundstueckId.id,
        this.appConfigService.einstellungHistorischeDaten,
        false,
        z.timepointIso,
        z.timepointId
      )
      .pipe(
        map((res) => {
          const rootBelastungenVm = new Array<RootBelastungVm>();
          for (const recht of res.entries) {
            const belastungen = recht.belastungen.filter(
              (b) => b.grundstueck.grundstueckId.id === grundstueckId.id
            );
            const entry = new RootBelastungVm(
              belastungen,
              grundstueckId,
              recht,
              this.getViewModelCtx(this.timepointService.timepointCtx)
            );
            rootBelastungenVm.push(entry);
          }
          return new GrundpfandrechtSichtGrundstueckVm(rootBelastungenVm, res);
        })
      );
  }

  private getViewModelCtx(z) {
    return <ViewModelCtx>{
      timepointId: z.timepointId,
      timepoint: z.timepointCtx,
      einstellungHistorischeDaten:
        this.appConfigService.einstellungHistorischeDaten,
      translateService: this.translateService,
      comparatorService: this.comparatorService,
    };
  }
}

interface ViewModelCtx {
  translateService: TranslateService;
  personService: GbpersonService;
  comparatorService: ComparatorService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepoint: string;
  timepointId: string;
}

export class GrundpfandrechtSichtGrundstueckVm {
  private readonly response: LoadPfandrechtSichtGrundstueckResponseMeta;

  constructor(
    public entries: RootBelastungVm[],
    response: LoadPfandrechtSichtGrundstueckResponseMeta
  ) {
    this.response = response;
    this.entries = entries;
  }

  get ersterfassungUebergeben() {
    return this.response.ersterfassungUebergeben;
  }
}

export class RootBelastungVm {
  pfandrechtMasterVm: PfandrechtMasterVm;
  otherBelastungen: PfandrechtBelastungMasterDetailEntryMeta[];

  constructor(
    public pfandrechtBelastungMasters: PfandrechtBelastungMasterDetailEntryMeta[],
    public grundstueckId: GrundstueckId,
    private pfandrechtMaster: PfandrechtMasterDetailEntryMeta,
    private ctx: ViewModelCtx
  ) {
    this.pfandrechtMasterVm = new PfandrechtMasterVm(
      pfandrechtMaster,
      pfandrechtBelastungMasters
    );
    this.otherBelastungen = this.calculateOtherBelastungen(
      pfandrechtMaster,
      grundstueckId
    );
  }

  get lastVersion() {
    const res = new Array<PfandrechtBelastungVersionDetailEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      res.push(...pfandrechtBelastungMaster.versions);
    }
    return res[res.length - 1];
  }

  get rechteBeziehungen() {
    const res = new Array<RechteBeziehungDetailEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      res.push(...pfandrechtBelastungMaster.rechteBeziehungen);
    }
    return res;
  }

  get teilpfandgruppen() {
    const res = new Array<Teilpfandgruppe>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      for (const version of pfandrechtBelastungMaster.versions) {
        if (version.teilpfandgruppe) {
          res.push(version.teilpfandgruppe);
        }
      }
    }
    return res;
  }

  get rangregelungen() {
    const res = new Array<RangregelungEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      for (const rangregelung of pfandrechtBelastungMaster.rangregelung) {
        if (rangregelung.vorgaengerPfandrechtBelastung) {
          res.push(rangregelung);
        }
      }
    }
    return res;
  }

  private calculateOtherBelastungen(
    pfandrechtMaster: PfandrechtMasterDetailEntryMeta,
    grundstueckId: GrundstueckId
  ) {
    return pfandrechtMaster.belastungen.filter(
      (b) => b.grundstueck.grundstueckId.id !== grundstueckId.id
    );
  }
}

export class PfandrechtMasterVm {
  versionenVm: PfandrechtVersionVm[];

  constructor(
    public object: PfandrechtMasterDetailEntryMeta,
    private pfandrechtBelastungMasters: PfandrechtBelastungMasterDetailEntryMeta[]
  ) {
    const pfandrechtBelastungVersions =
      new Array<PfandrechtBelastungVersionDetailEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      pfandrechtBelastungVersions.push(...pfandrechtBelastungMaster.versions);
    }
    this.versionenVm = new Array<PfandrechtVersionVm>();
    for (const version of object.versions) {
      const pfandrechtBelastungVersionen =
        this.calculatePfandrechtBelastungVersion(
          version.id,
          pfandrechtBelastungVersions
        );
      this.versionenVm.push(
        new PfandrechtVersionVm(version, object, pfandrechtBelastungVersionen)
      );
    }
  }

  public get lastVersion(): PfandrechtVersionVm {
    return this.versionenVm[this.versionenVm.length - 1];
  }

  private calculatePfandrechtBelastungVersion(
    versionId: PfandrechtVersionIdMeta,
    pfandrechtBelastungVersions: PfandrechtBelastungVersionDetailEntryMeta[]
  ) {
    return pfandrechtBelastungVersions.filter((v) =>
      v.pfandrechtVersionIds.map((y) => y.id).includes(versionId.id)
    );
  }
}

export class PfandrechtVersionVm {
  constructor(
    public object: PfandrechtVersionDetailEntryMeta,
    private master: PfandrechtMasterDetailEntryMeta,
    public pfandrechtBelastungVersions: PfandrechtBelastungVersionDetailEntryMeta[]
  ) {}

  getQueryParams() {
    return { id: this.master.id.id, versionId: this.object.id.id };
  }

  public get lastBelastungVersion(): PfandrechtBelastungVersionDetailEntryMeta {
    return this.pfandrechtBelastungVersions[
      this.pfandrechtBelastungVersions.length - 1
    ];
  }
}
